import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Chess } from 'chess.js';
import { api } from '@/lib/api';
import ChessBoardView from '@/components/chess/ChessBoardView';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  createTree, addMove, navigateTo, getNode, getChildren, getPath,
} from '@/lib/moveTree';
import { Save, ChevronLeft, RotateCcw, Undo2, Trash2 } from 'lucide-react';

function displayPlayerName(value, fallback) {
  const name = String(value ?? '').trim();
  return !name || ['?', 'unknown', '-'].includes(name.toLowerCase())
    ? fallback
    : name;
}

function removeBranch(tree, nodeId) {
  const node = tree.nodes[nodeId];
  if (!node || node.parentId == null) return tree;

  const parent = tree.nodes[node.parentId];
  if (!parent || parent.childrenIds[0] === nodeId) return tree;

  const removed = new Set();
  const collect = (id) => {
    if (removed.has(id)) return;
    removed.add(id);
    for (const childId of tree.nodes[id]?.childrenIds || []) {
      collect(childId);
    }
  };
  collect(nodeId);

  const nodes = { ...tree.nodes };
  for (const id of removed) delete nodes[id];

  nodes[parent.id] = {
    ...parent,
    childrenIds: parent.childrenIds.filter((id) => id !== nodeId),
  };

  return {
    ...tree,
    nodes,
    currentId: removed.has(tree.currentId) ? parent.id : tree.currentId,
  };
}

function serializeChildren(tree, parentId) {
  return getChildren(tree, parentId).map((node) => ({
    san: node.move.san,
    from: node.move.from,
    to: node.move.to,
    ...(node.move.promotion ? { promotion: node.move.promotion } : {}),
    fen_after: node.move.fen,
    children: serializeChildren(tree, node.id),
  }));
}

export default function PGNExerciseEditor({
  databaseId,
  game,
  onSaved,
  onClose,
}) {
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [orientation, setOrientation] = useState('white');
  const [revision, setRevision] = useState(0);
  const [savedRevision, setSavedRevision] = useState(0);
  const [undoStack, setUndoStack] = useState([]);

  useEffect(() => {
    let cancelled = false;

    async function loadTree() {
      if (!game?.id || !game?.pgn) {
        setTree(null);
        return;
      }

      setLoading(true);
      try {
        const { data } = await api.post('/pgn/variation-tree', {
          pgn: game.pgn,
        });

        if (cancelled) return;
        if (!data?.root_fen || !Array.isArray(data.children)) {
          throw new Error('El servidor no devolvió un árbol PGN válido');
        }

        let next = createTree(data.root_fen);

        // Cada nodo se agrega bajo su padre; el primer hijo conserva
        // su lugar como línea principal y los siguientes son alternativas.
        const addBranches = (parentId, children) => {
          for (const item of children || []) {
            const move = {
              san: item.san,
              from: item.from,
              to: item.to,
              fen: item.fen_after,
              ...(item.promotion ? { promotion: item.promotion } : {}),
            };

            next = addMove(next, parentId, move);
            const childId = next.currentId;
            addBranches(childId, item.children || []);
          }
        };

        addBranches(next.rootId, data.children);
        next = navigateTo(next, next.rootId);
        setTree(next);
        setRevision(0);
        setSavedRevision(0);
        setUndoStack([]);
      } catch (error) {
        if (!cancelled) {
          setTree(null);
          toast.error(
            error.response?.data?.detail ||
            error.message ||
            'No se pudo cargar el árbol',
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadTree();
    return () => { cancelled = true; };
  }, [game?.id, game?.pgn]);

  const currentNode = tree ? getNode(tree, tree.currentId) : null;
  const boardFen =
    currentNode?.move?.fen ||
    currentNode?.fen ||
    game?.starting_fen;

  const currentChildren = useMemo(
    () => tree && currentNode ? getChildren(tree, currentNode.id) : [],
    [tree, currentNode],
  );

  const path = useMemo(
    () => tree ? getPath(tree, tree.currentId) : [],
    [tree, tree?.currentId],
  );

  const handleDrop = useCallback((from, to) => {
    if (!tree || !currentNode || !boardFen) return false;

    try {
      const chess = new Chess(boardFen);
      const piece = chess.get(from);

      // Por ahora se utiliza promoción a dama automáticamente.
      const move = chess.move({
        from,
        to,
        ...(piece?.type === 'p' && (to.endsWith('8') || to.endsWith('1'))
          ? { promotion: 'q' }
          : {}),
      });

      if (!move) return false;

      const next = addMove(tree, currentNode.id, {
        san: move.san,
        from: move.from,
        to: move.to,
        fen: chess.fen(),
        ...(move.promotion ? { promotion: move.promotion } : {}),
      });

      if (next === tree) return false;

      setUndoStack((stack) => [...stack, tree]);
      setTree(next);
      setRevision((value) => value + 1);
      return true;
    } catch {
      return false;
    }
  }, [tree, currentNode, boardFen]);

  const undoLastMove = () => {
    if (!undoStack.length || saving) return;
    const previousTree = undoStack[undoStack.length - 1];
    setTree(previousTree);
    setUndoStack((stack) => stack.slice(0, -1));
    setRevision((value) => value + 1);
    toast.success('Última jugada deshecha');
  };

  const promoteVariation = (node) => {
    if (!tree || !node || saving) return;

    const parent = getNode(tree, node.parentId);
    if (!parent || !parent.childrenIds.includes(node.id)) {
      toast.error('No se pudo localizar la variante');
      return;
    }
    if (parent.childrenIds[0] === node.id) {
      toast.info('Esta variante ya es la principal');
      return;
    }

    const nextTree = {
      ...tree,
      nodes: {
        ...tree.nodes,
        [parent.id]: {
          ...parent,
          childrenIds: [
            node.id,
            ...parent.childrenIds.filter((id) => id !== node.id),
          ],
        },
      },
    };

    setUndoStack((stack) => [...stack, tree]);
    setTree(nextTree);
    setRevision((value) => value + 1);
    toast.success('Variante principal actualizada. Guarda los cambios para conservarla.');
  };

  const deleteVariation = (node) => {
    if (!tree || !node || saving) return;

    const parent = getNode(tree, node.parentId);
    if (!parent || parent.childrenIds[0] === node.id) {
      toast.error('La línea principal no se puede eliminar como variante');
      return;
    }

    if (!window.confirm(
      `¿Eliminar la variante que comienza con ${node.move.san} y todas sus continuaciones?`
    )) return;

    const next = removeBranch(tree, node.id);
    if (next === tree) {
      toast.error('No se pudo eliminar la variante');
      return;
    }

    setUndoStack((stack) => [...stack, tree]);
    setTree(next);
    setRevision((value) => value + 1);
    toast.success('Variante eliminada. Guarda los cambios para conservarlos.');
  };

  const save = async () => {
    if (!tree || !databaseId || !game?.id || saving) return;

    const revisionBeingSaved = revision;
    setSaving(true);
    try {
      const rootFen = tree.nodes[tree.rootId].fen;
      const payload = {
        starting_fen: rootFen,
        tree: {
          root_fen: rootFen,
          children: serializeChildren(tree, tree.rootId),
        },
        headers: {
          White: game.white_player || 'Unknown',
          Black: game.black_player || 'Unknown',
          Result: game.result || '*',
          Event: game.event || '',
          Site: game.site || '',
          Date: game.date || '',
          Round: game.round || '',
          ECO: game.eco || '',
          Opening: game.opening || '',
          Variation: game.variation || '',
          ...(game.white_elo != null
            ? { WhiteElo: String(game.white_elo) }
            : {}),
          ...(game.black_elo != null
            ? { BlackElo: String(game.black_elo) }
            : {}),
        },
      };

      await api.put(
        `/pgn-databases/${databaseId}/games/${game.id}/tree`,
        payload,
      );

      setSavedRevision(revisionBeingSaved);
      toast.success('Ejercicio y variantes guardados');
      if (onSaved) await onSaved();
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
        'No se pudo guardar el ejercicio',
      );
    } finally {
      setSaving(false);
    }
  };

  const goBack = () => {
    if (!tree || !currentNode || currentNode.parentId == null) return;
    setTree((value) => navigateTo(value, currentNode.parentId));
  };

  const hasPendingChanges = revision !== savedRevision;

  const resetNavigation = () => {
    if (tree) {
      setTree((value) => navigateTo(value, value.rootId));
    }
  };

  if (!game) return null;

  return (
    <section
      className="w-full rounded-xl border border-slate-200 bg-white p-4 space-y-4"
      data-testid="pgn-exercise-editor"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-semibold text-slate-900">Editor de ejercicio</h3>
          <p className="text-xs text-slate-500">
            {displayPlayerName(game.white_player, 'Blancas')} vs {displayPlayerName(game.black_player, 'Negras')}
            {' · '}Las variantes se guardan en esta partida.
            <span className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 font-medium ${saving ? 'bg-blue-100 text-blue-700' : hasPendingChanges ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-700'}`}>
              {saving ? 'Guardando…' : hasPendingChanges ? 'Cambios pendientes' : 'Guardado'}
            </span>
          </p>
        </div>

        <div className="flex gap-2">
          {onClose && (
            <Button size="sm" variant="outline" onClick={onClose}>
              Cerrar
            </Button>
          )}
          <Button
            size="sm"
            onClick={save}
            disabled={!tree || loading || saving}
          >
            <Save className="mr-1 h-4 w-4" />
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Cargando variantes…</p>
      ) : !tree ? (
        <p className="text-sm text-rose-600">
          No se pudo preparar el árbol de esta partida.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(280px,420px)_1fr]">
          <div className="space-y-3">
            <ChessBoardView
              fen={boardFen}
              boardWidth={400}
              orientation={orientation}
              arePiecesDraggable
              onPieceDrop={handleDrop}
            />

            <div className="flex flex-wrap items-center justify-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={goBack}
                disabled={currentNode.parentId == null}
              >
                <ChevronLeft className="mr-1 h-4 w-4" />
                Atrás
              </Button>

              <Button
                size="sm"
                variant="outline"
                onClick={undoLastMove}
                disabled={undoStack.length === 0 || saving}
              >
                <Undo2 className="mr-1 h-4 w-4" />
                Deshacer
              </Button>

              <Button size="sm" variant="outline" onClick={resetNavigation}>
                <RotateCcw className="mr-1 h-4 w-4" />
                Inicio
              </Button>

              <Button
                size="sm"
                variant="outline"
                onClick={() => setOrientation(
                  (value) => value === 'white' ? 'black' : 'white',
                )}
              >
                Girar tablero
              </Button>
            </div>

            <p className="text-center text-xs text-slate-500">
              Arrastra una pieza para añadir una jugada legal. Las jugadas
              nuevas se guardan cuando pulsas Guardar cambios.
            </p>
          </div>

          <div className="min-w-0 space-y-4">
            <div>
              <h4 className="mb-2 text-sm font-semibold">Ruta actual</h4>
              <div className="rounded-lg border bg-slate-50 p-3 text-sm font-mono break-words">
                {path.map((node, index) => (
                  <React.Fragment key={node.id}>
                    {index > 0 && (
                      <button
                        type="button"
                        className="mr-2 rounded px-1 hover:bg-amber-100"
                        onClick={() => setTree(
                          (value) => navigateTo(value, node.id),
                        )}
                      >
                        {node.move?.san}
                      </button>
                    )}
                  </React.Fragment>
                ))}
                {path.length === 1 && <span>Posición inicial</span>}
              </div>
            </div>

            <div>
              <h4 className="mb-2 text-sm font-semibold">
                Continuaciones desde esta posición ({currentChildren.length})
              </h4>

              {currentChildren.length === 0 ? (
                <p className="text-sm text-slate-500">
                  No hay continuaciones todavía. Añade una jugada en el tablero.
                </p>
              ) : (
                <div className="flex flex-col gap-2">
                  {currentChildren.map((node, index) => (
                    <div key={node.id} className="flex flex-wrap items-center gap-2">
                      <Button
                        size="sm"
                        variant={index === 0 ? 'default' : 'outline'}
                        onClick={() => setTree(
                          (value) => navigateTo(value, node.id),
                        )}
                      >
                        {index === 0 ? 'Principal: ' : 'Alternativa: '}
                        {node.move.san}
                      </Button>
                      {index > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={saving}
                          onClick={() => promoteVariation(node)}
                        >
                          Hacer principal
                        </Button>
                      )}
                      {index > 0 && (
                        <Button
                          size="icon"
                          variant="ghost"
                          title="Eliminar esta variante y sus continuaciones"
                          aria-label={`Eliminar variante ${node.move.san}`}
                          disabled={saving}
                          onClick={() => deleteVariation(node)}
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-lg border p-3">
              <h4 className="mb-1 text-sm font-semibold">Estado del árbol</h4>
              <p className="text-xs text-slate-600">
                Los cambios permanecen en memoria hasta guardar. Eliminar una
                variante no elimina la partida ni sus otras ramas.
              </p>
              <p className="mt-2 text-xs text-slate-500">
                Cambios de sesión: {revision}
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
